import { lightTheme } from "@allocine/ui-components"
import type { FieldPolicy, Operation } from "@apollo/client"
import { ApolloClient, ApolloLink, ApolloProvider, HttpLink, InMemoryCache, split } from "@apollo/client"
import { RetryLink } from "@apollo/client/link/retry"
import { GraphQLWsLink } from "@apollo/client/link/subscriptions"
import { getMainDefinition } from "@apollo/client/utilities"
import { Unavailable } from "@components/error"
import { ErrorBoundary } from "@components/error-boundary"
import { ThemeProvider } from "@mui/material"
import { AuthProvider, useSession } from "@utils/auth"
import { Kind, type OperationDefinitionNode, OperationTypeNode } from "graphql"
import { createClient } from "graphql-ws"
import type { ReactNode } from "react"
import { useMemo } from "react"
import { DndProvider } from "react-dnd"
import { HTML5Backend } from "react-dnd-html5-backend"
import { IntlProvider } from "react-intl"
import { ToastContainer } from "react-toastify"
import { BrowserCompatibilityChecker } from "./browser-compatibility-checker"
import { NotificationProvider } from "./components/notification"
import ScrollToHashElement from "./components/scrollToHashElement"
import { possibleTypes } from "./graphql/possible-types.json"
import { App } from "./pages/app"
import { ViewerProvider } from "./pages/shared/viewer"

const MAX_REQ_RETRIES = 30

type ApolloHttpError = Error & {
	statusCode: number
}

// biome-ignore lint/suspicious/noExplicitAny: large typeguard
const isApolloHttpError = (v: any): v is ApolloHttpError =>
	v && v instanceof Error && "statusCode" in v && typeof v.statusCode === "number"

const toWebSocketUrl = (httpUrl: string) => {
	const url = new URL(httpUrl)
	url.protocol = url.protocol === "https:" ? "wss:" : "ws:"
	return url.toString()
}

const isSubscription = ({ query }: Operation) => {
	const definition = getMainDefinition(query)
	return definition.kind === Kind.OPERATION_DEFINITION && definition.operation === OperationTypeNode.SUBSCRIPTION
}

const SecurityAwareApolloProvider = ({ children }: { children: ReactNode }) => {
	const session = useSession()
	const webSocketClient = useMemo(
		() =>
			createClient({
				url: toWebSocketUrl(__ENV__.GRAPH_BASE_URL),
				connectionParams: { authorization: `Bearer ${session?.accessToken}` },
			}),
		[session],
	)

	const client = useMemo(() => {
		const replace: FieldPolicy = {
			merge: (_, incoming) => incoming,
		}

		return new ApolloClient({
			cache: new InMemoryCache({
				possibleTypes,
				typePolicies: {
					PersonCreditsImportReport: {
						fields: {
							stats: { merge: true },
							resolution: { merge: true },
						},
					},
					Theater: {
						fields: {
							location: replace,
						},
					},
					Carousel: {
						fields: {
							slides: replace,
						},
					},
					Slide: {
						fields: {
							parents: replace,
						},
					},
					News: {
						fields: {
							authors: replace,
							publications: replace,
							pages: replace,
						},
					},
					User: {
						fields: {
							rights: replace,
						},
					},
					Query: {
						fields: {
							wysiwyg: replace,
						},
					},
				},
			}),
			link: split(
				isSubscription,
				new GraphQLWsLink(webSocketClient),
				ApolloLink.from([
					new RetryLink({
						delay: {
							initial: 150,
							max: 30 * 1000,
							jitter: true,
						},
						attempts: (count, operation, error) => {
							const operationType = (
								operation.query.definitions.find((def) => def.kind === "OperationDefinition") as OperationDefinitionNode
							).operation
							if (operationType !== OperationTypeNode.MUTATION && isApolloHttpError(error) && error.statusCode < 500)
								return false // do not retry 400 errors or below on queries
							if (error && operationType !== OperationTypeNode.MUTATION && count < MAX_REQ_RETRIES) return true // only retry queries
							return false // do not retry mutations
						},
					}),
					new ApolloLink((operation, forward) => {
						operation.setContext({
							headers: {
								// dynamically set authorization header
								Authorization: `Bearer ${session?.accessToken}`,
							},
							fetchOptions: {
								// send the actual referrer instead of the origin
								referrerPolicy: "unsafe-url",
							},
						})
						return forward(operation)
					}),
					new HttpLink({
						uri: __ENV__.GRAPH_BASE_URL,
					}),
				]),
			),
			defaultOptions: {
				watchQuery: {
					fetchPolicy: "network-only",
				},
			},
		})
	}, [session, webSocketClient])

	return <ApolloProvider client={client}>{children}</ApolloProvider>
}

export const Main = () => (
	<BrowserCompatibilityChecker>
		<ThemeProvider theme={lightTheme}>
			<ScrollToHashElement />
			<ErrorBoundary fallbackComponent={Unavailable}>
				<IntlProvider
					locale="en"
					formats={{
						date: {
							"date-time": {
								year: "numeric",
								month: "numeric",
								day: "numeric",
								hour: "numeric",
								minute: "numeric",
								second: "numeric",
								hour12: false,
							},
						},
					}}
				>
					<AuthProvider>
						<SecurityAwareApolloProvider>
							<ViewerProvider>
								<NotificationProvider maxStacked={3} position="top-right" closeAfterMs={3000}>
									<ToastContainer position="top-right" newestOnTop theme="colored" />
									<DndProvider backend={HTML5Backend}>
										<App />
									</DndProvider>
								</NotificationProvider>
							</ViewerProvider>
						</SecurityAwareApolloProvider>
					</AuthProvider>
				</IntlProvider>
			</ErrorBoundary>
		</ThemeProvider>
	</BrowserCompatibilityChecker>
)
